import React from 'react';
import { Leaf, Truck } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { getPackageLabel, getPackageDescription, getPackageStep } from '../../../lib/packageUtils';
import { toast } from 'sonner';
import { productsAPI } from '../../../lib/api';

const MONTHS = [
    { value: 1, label: 'Janvier' }, { value: 2, label: 'Fevrier' }, { value: 3, label: 'Mars' },
    { value: 4, label: 'Avril' }, { value: 5, label: 'Mai' }, { value: 6, label: 'Juin' },
    { value: 7, label: 'Juillet' }, { value: 8, label: 'Aout' }, { value: 9, label: 'Septembre' },
    { value: 10, label: 'Octobre' }, { value: 11, label: 'Novembre' }, { value: 12, label: 'Decembre' },
];

export const ProductForm = ({ isOpen, setIsOpen, editingProduct, formData, setFormData, categories, allSuppliers, onSubmit, onReset }) => (
    <Dialog open={isOpen} onOpenChange={(open) => { setIsOpen(open); if (!open) onReset(); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-sand" onInteractOutside={(e) => e.preventDefault()} onPointerDownOutside={(e) => e.preventDefault()}>
            <DialogHeader>
                <DialogTitle className="font-heading text-jungle flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-leaf/10 flex items-center justify-center">
                        <Leaf className="w-4 h-4 text-leaf" />
                    </div>
                    {editingProduct ? 'Modifier le produit' : 'Nouveau produit'}
                </DialogTitle>
            </DialogHeader>
            <form onSubmit={onSubmit} className="space-y-4">
                {/* Fournisseurs - en haut */}
                <div className="space-y-2 border-b pb-4">
                    <Label className="text-jungle flex items-center gap-2">
                        <Truck className="w-4 h-4" /><span>Fournisseur</span>
                        <span className="text-xs text-muted-foreground">(tapez le code fournisseur en prefixe, ex: F01-BAN001)</span>
                    </Label>
                    <div className="flex flex-wrap gap-2">
                        {allSuppliers.map(s => {
                            const isActive = formData.product_code?.toUpperCase().startsWith(s.code + '-');
                            return (
                                <button key={s.id} type="button"
                                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border transition-colors ${isActive ? 'bg-leaf/15 border-leaf text-jungle font-semibold' : 'bg-muted/50 border-transparent text-muted-foreground hover:bg-muted'}`}
                                    onClick={() => {
                                        const existing = formData.product_code || '';
                                        const stripped = existing.replace(/^[A-Z0-9]{3}-/, '');
                                        setFormData(prev => ({ ...prev, product_code: `${s.code}-${stripped}` }));
                                    }}>
                                    <span className="font-mono font-bold">{s.code}</span>
                                    <span>{s.name}</span>
                                </button>
                            );
                        })}
                    </div>
                    {formData.product_code && formData.product_code.length >= 4 && allSuppliers.some(s => formData.product_code.toUpperCase().startsWith(s.code + '-')) && (
                        <p className="text-xs text-emerald-600">
                            Fournisseur detecte : <strong>{allSuppliers.find(s => formData.product_code.toUpperCase().startsWith(s.code + '-'))?.name}</strong>
                        </p>
                    )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <Label htmlFor="product_code" className="text-jungle">Code Article</Label>
                        <Input id="product_code" value={formData.product_code}
                            onChange={(e) => setFormData({ ...formData, product_code: e.target.value.toUpperCase() })}
                            placeholder="Ex: F01-BAN001" className="border-jungle/20 rounded-xl font-mono" />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="gen_code" className="text-jungle">GENCOD (EAN)</Label>
                        <Input id="gen_code" value={formData.gen_code}
                            onChange={(e) => setFormData({ ...formData, gen_code: e.target.value.replace(/\D/g, '') })}
                            placeholder="Ex: 3760001234567" className="border-jungle/20 rounded-xl font-mono" maxLength={13} data-testid="gen-code-input" />
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <Label htmlFor="name" className="text-jungle">Nom du produit *</Label>
                        <Input id="name" value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            placeholder="Ex: Bananes Cavendish" className="border-jungle/20 rounded-xl" required />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="calibre" className="text-jungle">Calibre</Label>
                        <Input id="calibre" value={formData.calibre}
                            onChange={(e) => setFormData({ ...formData, calibre: e.target.value })}
                            placeholder="Ex: 70/80, Gros, Cat.1" className="border-jungle/20 rounded-xl" data-testid="calibre-input" />
                    </div>
                </div>
                <div className="space-y-2">
                    <Label htmlFor="description" className="text-jungle">Description *</Label>
                    <Textarea id="description" value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Description du produit..." className="border-jungle/20 rounded-xl" rows={3} required />
                </div>
                <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                        <Label htmlFor="price" className="text-jungle">Prix de vente (EUR) *</Label>
                        <Input id="price" type="number" step="0.01" min="0" value={formData.price}
                            onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                            placeholder="1.50" className="border-jungle/20 rounded-xl" required />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="purchase_price" className="text-jungle">Prix d'achat (EUR)</Label>
                        <Input id="purchase_price" type="number" step="0.01" min="0" value={formData.purchase_price}
                            onChange={(e) => setFormData({ ...formData, purchase_price: e.target.value })}
                            placeholder="0.80" className="border-jungle/20 rounded-xl" data-testid="purchase-price-input" />
                        {formData.price && formData.purchase_price && parseFloat(formData.purchase_price) > 0 && (
                            <p className={`text-xs font-medium ${parseFloat(formData.price) - parseFloat(formData.purchase_price) > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                                Marge : {(parseFloat(formData.price) - parseFloat(formData.purchase_price)).toFixed(2)} EUR ({((parseFloat(formData.price) - parseFloat(formData.purchase_price)) / parseFloat(formData.purchase_price) * 100).toFixed(0)}%)
                            </p>
                        )}
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="unit" className="text-jungle">Unite *</Label>
                        <Select value={formData.unit} onValueChange={(value) => setFormData({ ...formData, unit: value })}>
                            <SelectTrigger className="border-jungle/20 rounded-xl"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-white z-[100]">
                                {['kg', 'piece', 'chariot', 'carton', 'colis', 'palette', 'litre', 'bouquet', 'pack', 'sachet', 'barquette'].map(u => (
                                    <SelectItem key={u} value={u}>{u}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="package_weight" className="text-jungle">{getPackageLabel(formData.unit)}</Label>
                        <Input id="package_weight" type="number" step={getPackageStep(formData.unit)} min="0" placeholder="0"
                            value={formData.package_weight} onChange={(e) => setFormData({ ...formData, package_weight: e.target.value })}
                            className="border-jungle/20 rounded-xl" data-testid="package-weight-input" />
                        <p className="text-xs text-muted-foreground">{getPackageDescription(formData.unit)}</p>
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <Label htmlFor="min_order" className="text-jungle">Commande minimum (colis)</Label>
                        <Input id="min_order" type="number" min="1" value={formData.min_order}
                            onChange={(e) => setFormData({ ...formData, min_order: e.target.value })}
                            className="border-jungle/20 rounded-xl" />
                        <p className="text-xs text-muted-foreground">Quantite minimum en colis</p>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="stock" className="text-jungle">Stock disponible</Label>
                        <Input id="stock" type="number" min="0" value={formData.stock}
                            onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                            className="border-jungle/20 rounded-xl" />
                    </div>
                </div>
                <div className="space-y-2">
                    <Label htmlFor="category_id" className="text-jungle">Famille produit *</Label>
                    <Select value={formData.category_id} onValueChange={(value) => setFormData({ ...formData, category_id: value })}>
                        <SelectTrigger className="border-jungle/20 rounded-xl"><SelectValue placeholder="Selectionner une famille" /></SelectTrigger>
                        <SelectContent className="bg-white z-[100]">
                            {categories.map((cat) => (<SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="space-y-2">
                    <Label htmlFor="image_url" className="text-jungle">Image du produit</Label>
                    <Input id="image_url" value={formData.image_url}
                        onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                        placeholder="URL de l'image ou uploader ci-dessous" className="border-jungle/20 rounded-xl" />
                    <input type="file" accept="image/*" data-testid="product-image-upload"
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-jungle/10 file:text-jungle hover:file:bg-jungle/20 cursor-pointer"
                        onChange={async (e) => {
                            const file = e.target.files[0];
                            if (!file) return;
                            if (editingProduct) {
                                const fd = new FormData();
                                fd.append('file', file);
                                try {
                                    const res = await productsAPI.uploadImage(editingProduct.id, fd);
                                    setFormData(prev => ({ ...prev, image_url: res.data.image_url }));
                                    toast.success('Image uploadee');
                                } catch { toast.error("Erreur upload image"); }
                            } else {
                                const reader = new FileReader();
                                reader.onload = (ev) => setFormData(prev => ({ ...prev, image_url: ev.target.result }));
                                reader.readAsDataURL(file);
                            }
                        }} />
                    {formData.image_url && <img src={formData.image_url} alt="Preview" className="w-20 h-20 object-contain rounded-lg mt-2 bg-gray-50" />}
                </div>
                <div className="space-y-2">
                    <Label htmlFor="origin" className="text-jungle">Origine</Label>
                    <Input id="origin" value={formData.origin}
                        onChange={(e) => setFormData({ ...formData, origin: e.target.value })}
                        placeholder="Ex: France, Espagne..." className="border-jungle/20 rounded-xl" />
                </div>
                {/* Saison */}
                <div className="space-y-2 col-span-2">
                    <Label className="text-jungle flex items-center gap-2">
                        <span>Saison</span>
                        <span className="text-xs text-muted-foreground">(optionnel - pour les produits de saison)</span>
                    </Label>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label htmlFor="season_start" className="text-xs text-muted-foreground">De</Label>
                            <Select value={formData.season_start?.toString() || 'none'}
                                onValueChange={(value) => setFormData({ ...formData, season_start: value === 'none' ? '' : value })}>
                                <SelectTrigger className="border-jungle/20 rounded-xl"><SelectValue placeholder="Mois de debut" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">Non defini</SelectItem>
                                    {MONTHS.map((m) => (<SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label htmlFor="season_end" className="text-xs text-muted-foreground">A</Label>
                            <Select value={formData.season_end?.toString() || 'none'}
                                onValueChange={(value) => setFormData({ ...formData, season_end: value === 'none' ? '' : value })}>
                                <SelectTrigger className="border-jungle/20 rounded-xl"><SelectValue placeholder="Mois de fin" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">Non defini</SelectItem>
                                    {MONTHS.map((m) => (<SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    {formData.season_start && formData.season_end && formData.season_start !== 'none' && formData.season_end !== 'none' && (
                        <p className="text-xs text-emerald-600">
                            Saison: {MONTHS.find(m => m.value === parseInt(formData.season_start))?.label} a {MONTHS.find(m => m.value === parseInt(formData.season_end))?.label}
                        </p>
                    )}
                </div>
                <DialogFooter className="gap-2">
                    <Button type="button" variant="outline" onClick={() => setIsOpen(false)} className="rounded-xl">Annuler</Button>
                    <Button type="submit" className="bg-jungle hover:bg-jungle/90 text-sand rounded-xl">
                        {editingProduct ? 'Mettre a jour' : 'Creer le produit'}
                    </Button>
                </DialogFooter>
            </form>
        </DialogContent>
    </Dialog>
);
